import { createFileRoute } from "@tanstack/react-router";
import { asSearchString } from "@repo/utils/search-params";
import React, { useState } from "react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Button, Spinner } from "@heroui/react";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X } from "lucide-react";
import { ToastUtil } from "@/utils/toast";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { uploadFileToOss, buildCos } from "@/utils/cos/cos";
import { ReportAPI } from "@repo/api/report";
import { ReportType } from "@repo/api/report.type";
import { appConfig } from "@/config/app-config";
import { SafeAreaContainer } from "@/components/layout/safe-area-container";
import { useAuth } from "@repo/context/auth/auth";
import { Rsp } from "@/types/resp";
import { ReportTargetCard } from "./-components/ReportTargetCard";
import { Checkbox } from "@heroui/react";

export type ReportSearch = {
    target_id?: string;
    type?: string;
};

export const Route = createFileRoute("/report/")({
    validateSearch: (search: Record<string, unknown>): ReportSearch => ({
        target_id: asSearchString(search.target_id),
        type: asSearchString(search.type),
    }),
    component: ReportPage,
});


/**
 * 举报页面
 * 
 * URL参数：
 * - target_id: 被举报的目标ID
 * - type: 举报类型 (user | stall)
 */
export default function ReportPage() {
    useDocumentTitle("举报 - 兽聚汇");

    const reportRouteApi = getRouteApi("/report/");
    const { target_id, type } = reportRouteApi.useSearch();
    const navigate = useNavigate();
    const { userInfo } = useAuth();

    // URL参数
    const targetId = target_id || "";
    const reportType = (type as ReportType) || ReportType.USER;

    // 表单状态
    const [content, setContent] = useState("");
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
    const [isSelected, setIsSelected] = useState(false);
    // 处理文件选择
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles = Array.from(files);

            // 检查文件类型
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            const validFiles = newFiles.filter(file => {
                if (!allowedTypes.includes(file.type)) {
                    ToastUtil.showError(`${file.name} 文件类型不支持，仅支持图片文件`);
                    return false;
                }

                // 检查文件大小限制（每个文件最大10MB）
                const maxSize = 10 * 1024 * 1024; // 10MB
                if (file.size > maxSize) {
                    ToastUtil.showError(`${file.name} 文件过大，请选择小于10MB的文件`);
                    return false;
                }

                return true;
            });

            // 检查文件数量限制（最多9个文件）
            if (selectedFiles.length + validFiles.length > 9) {
                ToastUtil.showError("最多只能上传9个文件");
                return;
            }

            setSelectedFiles(prev => [...prev, ...validFiles]);
        }
        // 清空input值，允许重复选择同一文件
        event.target.value = "";
    };

    // 删除文件
    const handleDeleteFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // 提交举报
    const handleSubmit = async () => {
        if (!userInfo?.uid) {
            ToastUtil.showError("请先登录");
            return;
        }

        if (!content.trim()) {
            ToastUtil.showError("请填写举报内容");
            return;
        }

        if (content.trim().length < 10) {
            ToastUtil.showError("举报内容至少需要10个字符");
            return;
        }

        if (selectedFiles.length === 0) {
            ToastUtil.showError("请至少上传一张举证图片");
            return;
        }

        setIsSubmitting(true);

        // 初始化所有文件的上传进度为0
        const initialProgress: { [key: string]: number } = {};
        selectedFiles.forEach(file => {
            initialProgress[file.name] = 0;
        });
        setUploadProgress(initialProgress);

        // 上传所有文件到统一的 /temp 目录
        const uploadPromises = selectedFiles.map((file) => {
            // 生成唯一文件名，避免重名冲突
            const timestamp = Date.now();
            const randomStr = Math.random().toString(36).substring(2, 8);
            const fileExt = file.name.split('.').pop() || '';
            const fileName = `/temp/${userInfo.uid}/${timestamp}_${randomStr}.${fileExt}`;
            const cos = buildCos(fileName);
            return uploadFileToOss({
                cos,
                fileKey: fileName,
                file,
                bucket: appConfig.cos.mainBucket,
                region: appConfig.cos.mainBucketRegion,
                onProgress: (progress) => {
                    setUploadProgress(prev => ({
                        ...prev,
                        [file.name]: Math.round(progress.percent * 100)
                    }));
                }
            }).then((result) => {
                console.log(`${file.name} 上传完成:`, result);
                return fileName;
            }).catch((error) => {
                console.error(`${file.name} 上传失败:`, error);
                throw new Error(`${file.name} 上传失败: ${error.message}`);
            });
        });

        Promise.all(uploadPromises)
            .then((uploadedFileUrls) => {
                console.log('所有文件上传完成:', uploadedFileUrls);

                // 提交举报信息
                return ReportAPI.submitReport({
                    target_id: targetId,
                    type: reportType,
                    content: content.trim(),
                    evidence: uploadedFileUrls.filter(url => url),
                });
            })
            .then((response) => {
                // 清空表单和进度状态
                setContent("");
                setSelectedFiles([]);
                setUploadProgress({});

                // 跳转到举报成功页面，并携带举报ID
                const reportId = response.data.id;
                navigate({
                    to: "/result/success",
                    search: {
                        type: "success",
                        title: "举报提交成功",
                        end: "true",
                        op: "查看举报详情",
                        redirect: `/report/detail?id=${reportId}`,
                    },
                    replace: true,
                });
            })
            .catch((error: Rsp<null>) => {
                console.error('提交过程出错:', error);
                ToastUtil.showError(error.msg || "提交失败，请重试");
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <SafeAreaContainer className="flex flex-col gap-4 items-start justify-start p-4 relative mx-auto bg-gray-100 min-h-screen">
            {/* 页面标题 */}
            <div className="bg-white flex flex-col gap-2 items-start justify-start p-3 rounded-lg w-full">
                <div className="flex flex-col font-medium justify-center relative text-center text-black">
                    <p>举报信息</p>
                </div>
                <div className="flex flex-col font-normal justify-center leading-normal text-xs text-gray-600">
                    <p>
                        如果您发现了违规行为，您可以在这里进行举报，我们会认真审核每一条举报。平台实行“谁主张谁举证”原则，提交举报前，请确保您拥有充足的证据证明违规行为哦～
                    </p>
                </div>
            </div>

            {/* 举报目标信息展示 */}
            <div className="flex flex-col gap-1 items-start justify-start px-1 py-0 relative w-full">
                <div className="flex flex-col font-medium justify-center relative text-center text-black">
                    <p>举报对象</p>
                </div>
            </div>
            <ReportTargetCard
                targetId={targetId}
                type={reportType}
                onLoadError={(error) => ToastUtil.showError(error || "未知错误")}
            />

            {/* 举报内容 */}
            <div className="flex flex-col gap-1 items-start justify-start px-1 py-0 relative w-full">
                <div className="flex flex-col font-medium justify-center relative text-center text-black">
                    <p>
                        <span className="text-red-500">*</span>
                        <span>举报内容</span>
                    </p>
                </div>
                <div className="flex flex-col font-normal justify-center relative text-xs text-gray-500">
                    <p>请详细描述举报理由，字数限制2000字</p>
                </div>
            </div>

            {/* 举报内容输入框 */}
            <div className="bg-white flex flex-col gap-2 items-start justify-start relative rounded-lg w-full">
                <div className="flex gap-2.5 h-32 relative rounded-lg w-full">
                    <Textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="请详细描述您的举报理由，例如有什么样的违规行为，或者有什么样的违规商品等..."
                        className="h-full flex-1 resize-none border-0 focus-visible:ring-0 text-sm shadow-none"
                        maxLength={2000}
                        disabled={isSubmitting}
                    />
                    <div className="flex flex-col font-normal justify-center leading-none text-xs text-gray-500 absolute right-2 bottom-2">
                        <p className={content.length >= 2000 ? "text-red-500" : ""}>
                            {content.length}/2000
                        </p>
                    </div>
                </div>
            </div>

            {/* 举证内容 */}
            <div className="flex flex-col gap-1 items-start justify-start px-1 py-0 relative w-full">
                <div className="flex flex-col font-medium justify-center relative text-center text-black">
                    <p>
                        <span className="text-red-500">*</span>
                        <span>举证内容</span>
                    </p>
                </div>
                <div className="flex flex-col font-normal justify-center relative text-xs text-gray-500">
                    <p>请上传相关截图或录屏作为举证，至少上传1张，最多9张</p>
                </div>
            </div>

            {/* 图片上传区域 */}
            <div className="bg-white flex flex-col gap-2 items-start justify-start p-3 relative rounded-lg w-full">
                {/* 已选择的文件列表 */}
                <div className="grid grid-cols-3 gap-2 w-full">
                    {selectedFiles.map((file, index) => (
                        <div key={index} className="relative aspect-square">
                            <div className="w-full h-full bg-gray-100 rounded border flex items-center justify-center overflow-hidden">
                                <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    className="w-full h-full object-cover"
                                />
                                {/* 上传进度遮罩层 */}
                                {isSubmitting && uploadProgress[file.name] !== undefined && (
                                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                        <div className="text-white text-xs text-center">
                                            <div className="font-medium">{uploadProgress[file.name]}%</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => handleDeleteFile(index)}
                                disabled={isSubmitting}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                    {/* 添加图片按钮 */}
                    {selectedFiles.length < 9 && (
                        <div className="relative aspect-square">
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center rounded-lg hover:bg-gray-200 transition-colors border border-dashed border-gray-300">
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    id="file-upload"
                                    disabled={isSubmitting}
                                />
                                <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-1">
                                    <Plus className="w-6 h-6 text-gray-400" />
                                    <span className="text-xs text-gray-400">添加图片</span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>

                {/* 上传进度显示 */}
                {isSubmitting && selectedFiles.length > 0 && (
                    <div className="w-full mt-2">
                        <div className="text-xs text-gray-600 mb-1">
                            上传进度: {Object.values(uploadProgress).filter(progress => progress === 100).length} / {selectedFiles.length} 个文件完成
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                style={{
                                    width: `${selectedFiles.length > 0 ? (Object.values(uploadProgress).reduce((sum, progress) => sum + progress, 0) / selectedFiles.length) : 0}%`
                                }}
                            ></div>
                        </div>
                    </div>
                )}
            </div>

            {/* 确认声明 */}
            <div className="flex flex-col gap-2 items-start justify-start relative w-full">
                <div className="flex flex-col font-medium justify-center relative text-center text-black">
                    <p>
                        <span className="text-red-500">*</span>
                        <span>确认声明</span>
                    </p>
                </div>
                <Checkbox isSelected={isSelected} onChange={setIsSelected}>
                    <Checkbox.Content>
                        <Checkbox.Control>
                            <Checkbox.Indicator />
                        </Checkbox.Control>
                        <span className="text-sm">
                            我确认我提供的举报内容真实有效，且我拥有充足的证据证明违规行为
                        </span>
                    </Checkbox.Content>
                </Checkbox>
            </div>

            {/* 提交按钮 */}
            <div className="w-full">
                <Button
                    variant="danger"
                    onPress={() => void handleSubmit()}
                    isPending={isSubmitting}
                    isDisabled={isSubmitting || !content.trim() || selectedFiles.length === 0 || !isSelected}
                    className="w-full"
                >
                    {({ isPending }) => (
                        <>
                            {isPending && <Spinner size="sm" color="current" />}
                            {isPending ? (
                                selectedFiles.length > 0 ?
                                    `上传中... ${Object.values(uploadProgress).filter(progress => progress === 100).length}/${selectedFiles.length}` :
                                    "提交中..."
                            ) : "提交举报"}
                        </>
                    )}
                </Button>
            </div>
        </SafeAreaContainer>
    );
}

